import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { ArrowClockwiseRegular } from '@fluentui/react-icons';

import { CHANGE_STATUS, TChangeV2 } from '../../models';
import { getChangeV2WorkspacePath } from '../../helpers';
import { useChangeV2Store } from '../../state/ChangeV2Store';

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
});

export const ChangeAlertBar = ({ change }: { change: TChangeV2 }) => {
  const classes = useStyles();
  const { signedBaseline, saveFailureArmed, permissionDenied, retrySave } = useChangeV2Store();

  const bars: ReactNode[] = [];

  if (permissionDenied) {
    bars.push(
      <MessageBar key="permission" intent="error">
        <MessageBarBody>
          <MessageBarTitle>Permission denied</MessageBarTitle>
          You do not have permission to perform this action with the current role.
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (signedBaseline) {
    bars.push(
      <MessageBar key="signed" intent="warning">
        <MessageBarBody>
          <MessageBarTitle>Signed baseline</MessageBarTitle>
          This is a read-only signed snapshot. Editing is disabled.
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (saveFailureArmed) {
    bars.push(
      <MessageBar key="save" intent="error">
        <MessageBarBody>
          <MessageBarTitle>Autosave failed</MessageBarTitle>
          Your changes are kept locally. Retry to save them.
        </MessageBarBody>
        <MessageBarActions>
          <Button appearance="transparent" icon={<ArrowClockwiseRegular />} onClick={() => retrySave(change.id)}>
            Retry
          </Button>
        </MessageBarActions>
      </MessageBar>
    );
  }

  if (change.status === CHANGE_STATUS.DRAFT && change.returnInfo) {
    bars.push(
      <MessageBar key="returned" intent="warning">
        <MessageBarBody>
          <MessageBarTitle>Returned for clarification</MessageBarTitle>
          {change.returnInfo.rationale}
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (change.status === CHANGE_STATUS.CANCELLED && change.rejectInfo) {
    bars.push(
      <MessageBar key="rejected" intent="error">
        <MessageBarBody>
          <MessageBarTitle>Request rejected</MessageBarTitle>
          {change.rejectInfo.rationale}
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (change.status === CHANGE_STATUS.CANCELLED && change.duplicateInfo) {
    bars.push(
      <MessageBar key="duplicate" intent="warning">
        <MessageBarBody>
          <MessageBarTitle>Closed as a duplicate</MessageBarTitle>
          This change duplicates {change.duplicateInfo.duplicateOfDisplayId}. {change.duplicateInfo.rationale}
        </MessageBarBody>
        <MessageBarActions>
          <Link to={getChangeV2WorkspacePath(change.duplicateInfo.duplicateOfId)}>
            <Button appearance="transparent">Open canonical change</Button>
          </Link>
        </MessageBarActions>
      </MessageBar>
    );
  }

  if (change.awaitingExternalDecision) {
    bars.push(
      <MessageBar key="external" intent="info">
        <MessageBarBody>
          <MessageBarTitle>Awaiting external decision</MessageBarTitle>A notified body or authority decision is pending.
          This is an external gate, not an internal approval.
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (change.blockers.length > 0) {
    bars.push(
      <MessageBar key="blockers" intent="warning">
        <MessageBarBody>
          <MessageBarTitle>{change.blockers.length === 1 ? 'Blocker' : 'Blockers'}</MessageBarTitle>
          {change.blockers.join('. ')}
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (bars.length === 0) return null;

  return <div className={classes.root}>{bars}</div>;
};
